import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ICancelAccountRequest } from '@app/shared/models/banking.model';
import { IContract, IContractCancelReason } from '@app/shared/models/contract.model';
import { IDynamicDialogConfig } from '@app/shared/modules/dynamic-dialog/model';
import { SubscriberComponent } from '@app/shared/modules/subscriber/base/subscriber.component.base';
import { AmplitudeService } from '@app/shared/services/amplitude.service';
import { ContractContext } from '@app/shared/services/context/contract.context.service';
import { CustomerContext } from '@app/shared/services/context/customer.context.service';
import { ModalService } from '@app/shared/services/modal.service';
import { CardBasicService } from '@app/shared/services/wallet/card-basic.service';
import { STATUS } from '@app/shared/types/enums';
import { ACTION_EVENT_PROPERTY, ACTION_NAME, AMPLITUDE_EVENT } from '@app/shared/types/enums/amplitude';
import { AuthService, User } from '@auth0/auth0-angular';
import { catchError, concatMap, EMPTY, filter, map, Observable, of, Subject, switchMap, takeUntil, tap, timer } from 'rxjs';

@Component({
  selector: 'app-cancel-contract',
  templateUrl: './cancel-contract.component.html',
  styleUrls: ['./cancel-contract.component.sass'],
})
export class CancelContractComponent extends SubscriberComponent implements OnInit, OnDestroy {
  initialData!: IDynamicDialogConfig;
  contract!: IContract | null;
  rppUserId!: string;
  public isLoading = false;
  public cancelContractForm!: FormGroup;
  public reasons$: Observable<IContractCancelReason[] | undefined> = EMPTY;
  initialState!: IDynamicDialogConfig;
  STATUS = STATUS;
  private closeModal$ = new Subject<void>();

  constructor(
    private readonly _modalSvc: ModalService,
    public readonly _contractContextService: ContractContext,
    private readonly _cardBasicSvc: CardBasicService,
    private readonly _amplitudeSvc: AmplitudeService,
    private readonly _customerCtxSvc: CustomerContext,
    private readonly _authSvc: AuthService
  ) {
    super();
  }

  ngOnInit() {
    this.logInitEvent();
    this.getModalData();
    this.setupSubscriptions();
    this.createForm();
    this.setupModalCloseSubscription();
    this.contract = this._contractContextService.getCurrentContract();

    this.getCancelReasons();
  }

  private setupModalCloseSubscription(): void {
    this._subscriptions.sink = this._modalSvc
      .getModalData()
      .pipe(
        takeUntil(this.destroy$),
        filter(data => !!data),
        switchMap(() => this._modalSvc['_dialogRef'].afterClosed())
      )
      .subscribe(() => {
        this.closeModal();
      });
  }

  private logInitEvent(): void {
    this._amplitudeSvc.logEvent<ACTION_EVENT_PROPERTY>(AMPLITUDE_EVENT.ACTION_INIT, {
      actionName: ACTION_NAME.CANCEL_CONTRACT,
    });
  }

  private setupSubscriptions(): void {
    this._modalSvc.isPrimaryActionClicked$
      .pipe(
        takeUntil(this.closeModal$),
        filter(value => value),
        switchMap(() => this._authSvc.user$),
        switchMap(agentData => this.createCancelActionParams(agentData)),
        switchMap(params => this.executeCancelAction(params)),
        tap(() => this._modalSvc.closeWithPendingMsg()),
        catchError(() => {
          this._modalSvc.closeWithErrorMsg();

          return EMPTY;
        })
      )
      .subscribe(() => this.handleResponse());
  }

  private createCancelActionParams(agentData: User | null | undefined): Observable<{ user: any; body: ICancelAccountRequest }> {
    const email_support = agentData?.email || '';

    return this._customerCtxSvc.contextChanged$.pipe(
      takeUntil(this.closeModal$),
      map(user => {
        const body: ICancelAccountRequest = {
          reason: this.cancelContractForm.get('reason')?.value?.order_index || '',
          description: this.cancelContractForm.get('reason')?.value?.description || '',
          email_support,
        };

        return { user, body };
      })
    );
  }

  private handleResponse(): void {
    this._modalSvc.closeWithPendingMsg();

    this._subscriptions.sink = timer(2000) // wait for 2 seconds to make sure the contract is blocked/unblocked
      .pipe(
        takeUntil(this.closeModal$),
        concatMap(() => this._cardBasicSvc.getFilteredContracts(this.rppUserId)),
        concatMap(contracts => {
          const selectedContract = contracts.find(contract => contract.contract_code === this.contract?.contract_code);
          this._contractContextService.contractChange(selectedContract);

          return of(contracts);
        })
      )
      .subscribe(() => {
        this.closeModal();
      });
  }

  closeModal() {
    this.closeModal$.next();
    this.closeModal$.complete();
  }

  private executeCancelAction(params: { user: any; body: ICancelAccountRequest }): Observable<any> {
    const { user, body } = params;
    this.rppUserId = user?.rppUserId || '';
    const contractCode = this.contract?.contract_code || '';

    return this._cardBasicSvc.cancelAction(this.rppUserId, contractCode, body);
  }

  getCancelReasons() {
    this.reasons$ = this._cardBasicSvc.getCancelReasons(this.contract?.type || '') ?? EMPTY;
  }

  createForm() {
    this.cancelContractForm = new FormGroup({
      reason: new FormControl('', Validators.required),
    });
    this.onChangesForm();
  }

  onChangesForm() {
    this._subscriptions.sink = this.cancelContractForm.valueChanges.pipe(takeUntil(this.closeModal$)).subscribe(value => {
      if (this.cancelContractForm.valid) {
        const data = {
          ...this.initialState,
          modalComponentData: {
            ...this.initialState.modalComponentData,
            acceptButtonTitleDisabled: false,
            formData: value,
          },
        };

        this._modalSvc.setModalData(data);
      }
    });
  }

  getModalData() {
    this._subscriptions.sink = this._modalSvc
      .getModalData()
      .pipe(takeUntil(this.closeModal$))
      .subscribe({
        next: (data: IDynamicDialogConfig) => (this.initialState = data),
      });
  }

  ngOnDestroy() {
    this.closeModal();
    this.unsubscribe();
  }
}
